/**
 * Environmental Geotech — Operations Dashboard
 *
 * Cross-step KPI summary for the full 8-step intelligence loop.
 * Aggregates live data from protected zones, anomalies, missions,
 * enforcement actions, and carbon credit missions to give Environmental
 * Managers a single situational-awareness view.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  TreePine, AlertTriangle, Navigation, Scale,
  RefreshCw, Mountain, MapPin, Leaf, ShieldAlert,
} from 'lucide-react';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import { KpiCard, StepPill, ActionBanner } from '../../components/common/OperationsDashboard';
import { useNavigate } from 'react-router-dom';
import { anomalyApi, satelliteBoundariesApi } from '../../services/api';
import api from '../../services/api';

interface DashboardData {
  zones:              number;
  pendingAnomalies:   number;
  criticalAnomalies:  number;
  activeSurveys:      number;
  awaitingReview:     number;
  openEnforcements:   number;
  geohazardEvents:    number;
  carbonMissions:     number;
}

export const EnvDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData]       = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [anomalyStats, boundaryRes, missionRes, geohazardRes] = await Promise.all([
        anomalyApi.getStats('environmental'),
        satelliteBoundariesApi.getAll(),
        api.get('/missions', { params: { limit: 200 } }),
        anomalyApi.getAnomalies({ vertical: 'environmental', status: 'pending' }),
      ]);

      const zones = (boundaryRes.data?.boundaries ?? []).filter(
        (b: any) => b.metadata?.vertical === 'environmental',
      ).length;

      const missions: any[] = missionRes.data?.missions ?? missionRes.data?.data?.missions ?? [];
      const envMissions     = missions.filter((m) => m.metadata?.vertical === 'environmental');

      const activeSurveys = envMissions.filter(
        (m) => m.mission_type === 'drone_survey' && (m.status === 'in_progress' || m.status === 'pending'),
      ).length;

      const awaitingReview = envMissions.filter(
        (m) => m.metadata?.ai_analysis_status === 'complete' && !m.metadata?.step7_status,
      ).length;

      const openEnforcements = envMissions.filter(
        (m) => m.metadata?.step7_status && !m.metadata?.enforcement_outcome,
      ).length;

      const geohazardEvents = (geohazardRes.anomalies ?? []).filter(
        (a: any) =>
          a.anomaly_type?.includes('landslide') ||
          a.anomaly_type?.includes('slope') ||
          a.anomaly_type?.includes('flood'),
      ).length;

      const carbonMissions = envMissions.filter(
        (m) => m.metadata?.co2_sequestration_t != null || m.metadata?.agb_tonnes_ha != null,
      ).length;

      setData({
        zones,
        pendingAnomalies:  anomalyStats?.byStatus?.pending    ?? 0,
        criticalAnomalies: anomalyStats?.bySeverity?.critical ?? 0,
        activeSurveys,
        awaitingReview,
        openEnforcements,
        geohazardEvents,
        carbonMissions,
      });
      setLastRefresh(new Date());
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const actionRequired = (data?.pendingAnomalies ?? 0) + (data?.awaitingReview ?? 0) + (data?.openEnforcements ?? 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <TreePine className="w-7 h-7 text-teal-500" /> Environmental Dashboard
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Live situational overview across the full 8-step environmental intelligence loop
          </p>
        </div>
        <div className="flex items-center gap-2">
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Updated {lastRefresh.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
          </p>
          <Button variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}
      {!loading && <ActionBanner count={actionRequired} />}

      {loading ? (
        <div className="flex justify-center py-14"><Spinner size="lg" /></div>
      ) : data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            <KpiCard label="Zones Monitored"    value={data.zones}            icon={MapPin}        colour="bg-teal-100 dark:bg-teal-900/30 text-teal-600"    onClick={() => navigate('/environmental/boundaries')} />
            <KpiCard label="Pending Anomalies"  value={data.pendingAnomalies} icon={AlertTriangle} colour={data.pendingAnomalies > 0 ? 'bg-red-100 dark:bg-red-900/30 text-red-600' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'} onClick={() => navigate('/environmental/anomalies')} />
            <KpiCard label="Active Surveys"     value={data.activeSurveys}    icon={Navigation}    colour="bg-blue-100 dark:bg-blue-900/30 text-blue-600"    onClick={() => navigate('/environmental/flight-analysis')} />
            <KpiCard label="Open Enforcements"  value={data.openEnforcements} icon={Scale}         colour={data.openEnforcements > 0 ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'} onClick={() => navigate('/environmental/enforcement')} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-3">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Intelligence Loop — Action Queue</p>
              <StepPill step="1"   label="Zone Boundaries"          path="/environmental/boundaries" />
              <StepPill step="2"   label="Temporal Analysis"         path="/environmental/temporal" />
              <StepPill step="3–4" label="Anomaly Alerts"            count={data.pendingAnomalies}  urgent path="/environmental/anomalies" />
              <StepPill step="5"   label="Survey Mission Planner"    path="/environmental/survey-planner" />
              <StepPill step="6"   label="Flight Data & Analysis"    path="/environmental/flight-analysis" />
              <StepPill step="7"   label="Analysis Review"           count={data.awaitingReview}    urgent path="/environmental/analysis-review" />
              <StepPill step="8"   label="Enforcement Actions"       count={data.openEnforcements}  urgent path="/environmental/enforcement" />
            </div>

            <div className="space-y-4">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Summary Metrics</p>

              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Critical Anomalies</p>
                  {data.criticalAnomalies > 0 && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">Urgent</span>}
                </div>
                <p className={`text-3xl font-bold mt-1 ${data.criticalAnomalies > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>{data.criticalAnomalies}</p>
              </div>

              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
                <p className="text-xs text-gray-500 dark:text-gray-400">Awaiting Analysis Review (Step 7)</p>
                <p className={`text-3xl font-bold mt-1 ${data.awaitingReview > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-white'}`}>{data.awaitingReview}</p>
              </div>

              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
                <p className="text-xs text-gray-500 dark:text-gray-400">Carbon Credit Surveys</p>
                <p className="text-3xl font-bold mt-1 text-teal-600 dark:text-teal-400">{data.carbonMissions}</p>
                {data.geohazardEvents > 0 && <p className="text-xs text-orange-500 dark:text-orange-400 mt-1">{data.geohazardEvents} active geohazard event{data.geohazardEvents !== 1 ? 's' : ''}</p>}
              </div>

              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Quick Links</p>
                {[
                  { label: 'Carbon Credit Verification', path: '/environmental/carbon',       icon: Leaf },
                  { label: 'Geohazard Risk',              path: '/environmental/geohazard',    icon: Mountain },
                  { label: 'Enforcement Actions',         path: '/environmental/enforcement',  icon: ShieldAlert },
                  { label: 'Analysis Review',             path: '/environmental/analysis-review', icon: Scale },
                ].map(({ label, path, icon: Icon }) => (
                  <button key={path} onClick={() => navigate(path)}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                    <Icon className="w-4 h-4 text-teal-500 flex-shrink-0" />
                    {label}
                    <span className="ml-auto text-gray-400">›</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EnvDashboardPage;

// Made with Bob
